import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import { FormattedMessage, injectIntl } from 'react-intl';
import getObjectProperty from 'lodash/get';

import { Container, Header, Grid, Button } from 'semantic-ui-react';
import Svg from 'react-inlinesvg';

import { GridNumbers } from '../../utils';

import { PageHeader, Wrappers } from '../../components';
import { authProptypes } from '../../auth';
import { withPageViewTracking } from '../../analytics';
import { withAuth } from '../../auth';
import { withAnalytics } from '../../analytics';
import { withNavigator } from '../../routing';
import { Queries } from '../../api';
import { digitalMedia, traditionalPrint, physical } from '../../images';
import {
  OverviewWrapper,
  IconContainer,
  OverviewIcon,
  TextColumn,
  ContactParagraph,
  ButtonContainer
} from './DealerSuccess.style';

const DealerSuccess = () => {
  const renderOverviewSection = (
    img,
    subtitleID,
    subtitleMsg,
    descID,
    descMsg
  ) => (
    <Grid>
      <Grid.Column mobile={GridNumbers.base} tablet={4} computer={4}>
        <IconContainer>
          <OverviewIcon size="massive">
            <Svg src={img} />
          </OverviewIcon>
        </IconContainer>
      </Grid.Column>
      <TextColumn mobile={GridNumbers.base} tablet={12} computer={12}>
        <h3>
          <FormattedMessage
            id={`subtitle.${subtitleID}`}
            defaultMessage={subtitleMsg}
          />
        </h3>
        <p>
          <FormattedMessage
            id={`description.${descID}`}
            defaultMessage={descMsg}
          />
        </p>
      </TextColumn>
    </Grid>
  );

  return (
    <Wrappers.Layout menu>
      <Container as="main">
        <PageHeader overlap>
          <Header as="h1" data-qa="dealer-success-header">
            <small>Marketing</small>
            <FormattedMessage
              id="title.dealer_success"
              defaultMessage="Dealer Success Program"
            />
          </Header>
        </PageHeader>
        <Grid>
          <Grid.Column mobile={GridNumbers.base} computer={GridNumbers.half}>
            <section className="bg--secondary bg--padded">
              <header>
                <h2>
                  <FormattedMessage
                    id="subtitle.dealer_success_overview"
                    defaultMessage="An Overview"
                  />
                </h2>
              </header>
              <p>
                <FormattedMessage
                  id="description.dealer_success_overview_1"
                  defaultMessage="The AGI Dealer Success Program is designed to help you grow your business by assisting your sales and marketing initiatives. Our goal is simple, to make doing business with us easier and to help you succeed."
                />
              </p>
              <p>
                <FormattedMessage
                  id="description.dealer_success_overview_2"
                  defaultMessage="In short, you may qualify for co-pay marketing dollars based on annual sales to support your marketing initiatives."
                />
              </p>
              <p>
                Looking for the latest AGI marketing assets to promote your
                company?&nbsp;
                <a href="https://agidealersupport.zendesk.com/hc/en-us/categories/360005747753-Dealer-Success-Program-and-Marketing-Assets">
                  Click here to access
                </a>
              </p>
              <OverviewWrapper>
                {renderOverviewSection(
                  traditionalPrint,
                  'dealer_success_overview_A',
                  'Traditional print',
                  'dealer_success_overview_3',
                  'From newspapers and magazines, to specialized guides on direct marketing using the postal service.'
                )}
                {renderOverviewSection(
                  digitalMedia,
                  'dealer_success_overview_B',
                  'Digital and media',
                  'dealer_success_overview_4',
                  'We cover social media sponsored posts, banner ads on relevant websites as well as selected TV or radio commercials.'
                )}
                {renderOverviewSection(
                  physical,
                  'dealer_success_overview_C',
                  'Physical',
                  'dealer_success_overview_5',
                  'For all your events and tradeshow needs such as banner displays or AGI promotional items such as hats, shirts and keychains.'
                )}
              </OverviewWrapper>
              <h3>
                <FormattedMessage
                  id={`subtitle.dealer_success_overview_D`}
                  defaultMessage="And more!"
                />
              </h3>
              <p>
                <FormattedMessage
                  id="description.dealer_success_overview_6"
                  defaultMessage="It’s your ideas and your knowledge of our customers that helps us increase awareness of our brands. Any idea you have is welcomed and can be submitted for a pre-approval by our marketing team."
                />
              </p>
              <ContactParagraph>
                Email{' '}
                <a href="mailto:agidealersupport@aggrowth.com">
                  agidealersupport@aggrowth.com
                </a>{' '}
                for more information
              </ContactParagraph>
            </section>
          </Grid.Column>
          <Grid.Column mobile={GridNumbers.base} computer={GridNumbers.half}>
            <section className="bg--secondary bg--padded">
              <header>
                <h2>
                  <FormattedMessage
                    id="subtitle.dealer_success_program"
                    defaultMessage="Join the Dealer Success Program"
                  />
                </h2>
              </header>
              <p>
                <FormattedMessage
                  id="description.dealer_success_program"
                  defaultMessage="We made a comprehensive PDF for you to learn about all the ways we can help, the different levels of financial support we can provide and all the details regarding the process to follow in regards to your marketing expenses."
                />
              </p>
              <ButtonContainer>
                <Button
                  as="a"
                  href="/pdf/agi_dealer_success.pdf"
                  target="_blank"
                  primary
                  size="small"
                  data-qa="warranty-claim-restart"
                >
                  Download Detailed PDF
                </Button>
                <Button
                  as="a"
                  href="http://aggrowth.com/dealersuccess"
                  target="_blank"
                  secondary
                  size="small"
                >
                  Register Here
                </Button>
              </ButtonContainer>
            </section>
          </Grid.Column>
        </Grid>
      </Container>
    </Wrappers.Layout>
  );
};

DealerSuccess.propTypes = {
  authUser: authProptypes.user,
  brandsList: PropTypes.array
};

export default withPageViewTracking()(
  injectIntl(
    withNavigator(
      withAuth(
        withAnalytics(
          graphql(Queries.getBrands, {
            props: props => {
              const brands = getObjectProperty(
                props,
                'data.getBrands',
                undefined
              );
              const brandsList =
                brands && brands.filter(brand => brand.farmBrand);
              return {
                ...props,
                brandsList
              };
            }
          })(DealerSuccess)
        )
      )
    )
  )
);
